import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Users } from "lucide-react";

import CollaboratorsRevenueTable from "@/components/pilotage/CollaboratorsRevenueTable";
import AppErrorState from "@/components/ui/AppErrorState";
import { Button } from "@/components/ui/button";
import { getApiErrorMessage } from "@/lib/api";
import { subscribeTimesheetUpdated } from "@/lib/dataEvents";
import { getPilotageCockpit } from "@/lib/pilotageApi";
import { normalizeCollaboratorsRevenueRows } from "@/lib/pilotageCollaborators";

const STATUS_FILTERS = [
  { value: "ALL", label: "Tous les statuts" },
  { value: "EN_MISSION", label: "En mission" },
  { value: "INTERCONTRAT", label: "Intercontrat" },
  { value: "SORTI", label: "Sortis" },
];

const MONTH_OPTIONS = [
  { value: 1, label: "Janvier" },
  { value: 2, label: "Février" },
  { value: 3, label: "Mars" },
  { value: 4, label: "Avril" },
  { value: 5, label: "Mai" },
  { value: 6, label: "Juin" },
  { value: 7, label: "Juillet" },
  { value: 8, label: "Août" },
  { value: 9, label: "Septembre" },
  { value: 10, label: "Octobre" },
  { value: 11, label: "Novembre" },
  { value: 12, label: "Décembre" },
];

const CollaboratorsRevenuePage = () => {
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState(() => {
    const requested = Number(new URLSearchParams(window.location.search).get("year"));
    return [2026, 2027].includes(requested) ? requested : Math.min(2027, Math.max(2026, currentYear));
  });
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const requestedMonth = Number(params.get("month"));
    if (Number.isInteger(requestedMonth) && requestedMonth >= 1 && requestedMonth <= 12) {
      return requestedMonth;
    }
    const requestedYear = Number(params.get("year"));
    const year = [2026, 2027].includes(requestedYear) ? requestedYear : Math.min(2027, Math.max(2026, currentYear));
    if (year === currentYear) {
      return new Date().getMonth() + 1;
    }
    return 1;
  });
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [requestKey, setRequestKey] = useState(0);

  const updateUrlParams = (year, month) => {
    const params = new URLSearchParams(window.location.search);
    params.set("year", String(year));
    params.set("month", String(month));
    window.history.replaceState(null, "", `${window.location.pathname}?${params.toString()}`);
  };

  const filteredRows = useMemo(() => {
    if (statusFilter === "ALL") {
      return rows;
    }
    return rows.filter((row) => {
      const s = String(row?.status || "").toUpperCase();
      if (statusFilter === "EN_MISSION") {
        return s === "EN MISSION";
      }
      if (statusFilter === "INTERCONTRAT") {
        return s === "INTERCONTRAT";
      }
      if (statusFilter === "SORTI") {
        return s === "SORTI" || s === "INACTIF";
      }
      return true;
    });
  }, [rows, statusFilter]);

  useEffect(() => {
    let cancelled = false;

    const loadData = async () => {
      setLoading(true);
      setError("");

      try {
        const cockpit = await getPilotageCockpit({ year: selectedYear, month: selectedMonth });

        if (cancelled) {
          return;
        }

        setRows(normalizeCollaboratorsRevenueRows(cockpit?.tables?.collaborators_revenue));
      } catch (requestError) {
        if (cancelled) {
          return;
        }

        setError(
          getApiErrorMessage(requestError, "Impossible de charger les collaborateurs."),
        );
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void loadData();

    return () => {
      cancelled = true;
    };
  }, [requestKey, selectedYear, selectedMonth]);

  useEffect(() => {
    const unsubscribe = subscribeTimesheetUpdated(() => {
      setRequestKey((current) => current + 1);
    });

    return unsubscribe;
  }, []);

  return (
    <div className="space-y-6" data-testid="pilotage-collaborators-revenue-page">
      <div className="flex flex-wrap items-center gap-3">
        <Button
          asChild
          variant="outline"
          className="border-white/10 text-slate-200 hover:bg-white/5 hover:text-white"
        >
          <Link to={`/admin/pilotage/dashboard?year=${selectedYear}#pilotage-collaborateurs`}>
            <ArrowLeft className="h-4 w-4" />
            Retour au dashboard
          </Link>
        </Button>
      </div>

      <section className="rounded-3xl border border-white/10 glass-heavy p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="flex items-center gap-2 text-2xl font-heading font-bold text-white">
              <Users className="h-5 w-5 text-lenvia-blue" />
              Collaborateurs (CA & missions)
            </h1>
            <p className="mt-2 max-w-3xl text-sm text-slate-400">
              Vue de l’exercice {selectedYear}. Les frais ponctuels des autres années sont exclus ; les frais récurrents encore ouverts restent prévus sur cette année.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <label className="text-xs font-semibold uppercase tracking-wider text-slate-400">
              Statut
              <select
                aria-label="Filtre statut"
                value={statusFilter}
                onChange={(event) => setStatusFilter(event.target.value)}
                className="mt-2 block h-10 rounded-lg border border-white/10 bg-slate-900 px-3 text-sm text-white"
                data-testid="pilotage-collaborators-status-filter"
              >
                {STATUS_FILTERS.map((filter) => (
                  <option key={filter.value} value={filter.value}>{filter.label}</option>
                ))}
              </select>
            </label>
            <label className="text-xs font-semibold uppercase tracking-wider text-slate-400">
              Mois
              <select
                aria-label="Mois de revenu"
                value={selectedMonth}
                onChange={(event) => {
                  const month = Number(event.target.value);
                  setSelectedMonth(month);
                  updateUrlParams(selectedYear, month);
                }}
                className="mt-2 block h-10 rounded-lg border border-white/10 bg-slate-900 px-3 text-sm text-white"
                data-testid="pilotage-collaborators-month-filter"
              >
                {MONTH_OPTIONS.map((m) => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </select>
            </label>
            <label className="text-xs font-semibold uppercase tracking-wider text-slate-400">
              Année
              <select
                aria-label="Année de revenu"
                value={selectedYear}
                onChange={(event) => {
                  const year = Number(event.target.value);
                  setSelectedYear(year);
                  updateUrlParams(year, selectedMonth);
                }}
                className="mt-2 block h-10 rounded-lg border border-white/10 bg-slate-900 px-3 text-sm text-white"
              >
                {[2026, 2027].map((year) => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </label>
          </div>
        </div>
      </section>

      {loading ? (
        <section
          className="rounded-3xl border border-white/10 glass p-6"
          data-testid="pilotage-collaborators-revenue-loading"
        >
          <CollaboratorsRevenueTable
            rows={[]}
            isLoading
            tableTestId="pilotage-collaborators-revenue-table"
          />
        </section>
      ) : null}

      {!loading && error ? (
        <AppErrorState
          title="Collaborateurs indisponibles"
          message={error}
          onRetry={() => setRequestKey((current) => current + 1)}
          testId="pilotage-collaborators-revenue-error"
        />
      ) : null}

      {!loading && !error ? (
        <section className="rounded-3xl border border-white/10 glass p-6">
          <CollaboratorsRevenueTable
            year={selectedYear}
            month={selectedMonth}
            rows={filteredRows}
            tableTestId="pilotage-collaborators-revenue-table"
          />
        </section>
      ) : null}
    </div>
  );
};

export default CollaboratorsRevenuePage;
